import { useMutation, useQueryClient } from '@tanstack/react-query'
import { sendMessage } from '../../../data/statsAssistant'
import {
  SendMessagePayload,
  StatsAssistantRequestError,
  TURN_IN_PROGRESS_CODE,
} from '../../../model/StatsAssistant'
import { statsAssistantKeys } from './statsAssistant.keys'
import {
  keepFailedOptimisticMessage,
  settleOptimisticMessage,
  showOptimisticMessage,
} from './statsAssistant.utils'

export type SendMessageVariables = SendMessagePayload & {
  conversationUlid: string
}

export const useSendMessage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ conversationUlid, ...payload }: SendMessageVariables) => {
      const { data, ok, status } = await sendMessage(conversationUlid, payload)
      if (!ok || !data || !('userMessage' in data)) {
        const error = data && 'code' in data ? data : null
        throw new StatsAssistantRequestError(
          status ?? 0,
          error?.code ?? null,
          error?.userMessage ?? null,
        )
      }
      return data.userMessage
    },
    onMutate: ({ conversationUlid, ...payload }) =>
      showOptimisticMessage(queryClient, conversationUlid, payload),
    onSuccess: (userMessage, { conversationUlid }) =>
      settleOptimisticMessage(queryClient, conversationUlid, userMessage),
    onError: (error, { conversationUlid }) => {
      keepFailedOptimisticMessage(queryClient, conversationUlid)
      // Tour lancé depuis un autre onglet : le fil rechargé reflète l'état réel.
      if (error instanceof StatsAssistantRequestError && error.code === TURN_IN_PROGRESS_CODE) {
        void queryClient.invalidateQueries({
          queryKey: statsAssistantKeys.conversation(conversationUlid),
        })
      }
    },
  })
}
